// ============================================================================
// marketDisputeTags — СОСТОЯНИЕ спора на заявке (существующий механизм тегов).
//
// Открыт/решён — два тега базы. Теги не меняют запись заявки и не требуют
// миграций; оператор видит очередь через ListSubjectsByTag. При записи
// сохраняются ВСЕ прочие теги заявки (полная замена набора — с объединением).
// ============================================================================

import { listSubjectTags, listTags, saveTag, setSubjectTags, type TagInfo } from '@/lib/tags';
import { DISPUTE_SUBJECT_CODE, DISPUTE_SUBJECT_TYPE } from './marketDisputeConstants';

export const DISPUTE_OPEN_TAG = 'Спор маркетплейса';
export const DISPUTE_CLOSED_TAG = 'Спор маркетплейса: решён';

function normalizedName(value: string): string {
  return String(value || '').trim().toLowerCase();
}

/** Все теги с ТОЧНЫМ именем (гонка создания могла дать дубликаты — очередь
 *  объединяет их, чтобы заявки не «пропадали»). */
export async function findDisputeTagIds(name: string): Promise<string[]> {
  const tags = await listTags(name);
  return tags
    .filter((t) => normalizedName(t.name) === normalizedName(name) && t.id)
    .map((t) => t.id);
}

export async function findDisputeTagId(name: string): Promise<string> {
  const ids = await findDisputeTagIds(name);
  return ids[0] || '';
}

async function ensureTag(name: string, color: string): Promise<string> {
  const existing = await findDisputeTagId(name);
  if (existing) return existing;
  const id = await saveTag({ name, color });
  if (id) return id;
  const again = await findDisputeTagId(name);
  if (!again) throw new Error(`Не удалось создать тег «${name}»`);
  return again;
}

export function disputeTagState(tags: TagInfo[]): { open: boolean; closed: boolean } {
  return {
    open: tags.some((t) => normalizedName(t.name) === normalizedName(DISPUTE_OPEN_TAG)),
    closed: tags.some((t) => normalizedName(t.name) === normalizedName(DISPUTE_CLOSED_TAG)),
  };
}

export async function readDisputeTagState(requestId: string): Promise<{ open: boolean; closed: boolean }> {
  const tags = await listSubjectTags({
    subjectType: DISPUTE_SUBJECT_TYPE,
    subjectCode: DISPUTE_SUBJECT_CODE,
    subjectId: requestId,
  });
  return disputeTagState(tags);
}

/** Выставить состояние спора, сохранив остальные теги заявки. */
export async function applyDisputeTags(requestId: string, open: boolean, closed: boolean): Promise<void> {
  const ref = { subjectType: DISPUTE_SUBJECT_TYPE, subjectCode: DISPUTE_SUBJECT_CODE, subjectId: requestId };
  const current = await listSubjectTags(ref);
  const keep = current
    .filter((t) => (
      normalizedName(t.name) !== normalizedName(DISPUTE_OPEN_TAG)
      && normalizedName(t.name) !== normalizedName(DISPUTE_CLOSED_TAG)
    ))
    .map((t) => t.id);
  const ids = [...keep];
  if (open) ids.push(await ensureTag(DISPUTE_OPEN_TAG, '#dc2626'));
  if (closed) ids.push(await ensureTag(DISPUTE_CLOSED_TAG, '#059669'));
  await setSubjectTags(ref, Array.from(new Set(ids)));
}
